import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { renderMetadataValue } from '@/helpers';
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { sortAscByProperty } from '@/utils/array.utils';
import type { MetadataGlossaryType, MetadataPostItem } from '@/model/Metadata.model';
import type { Sample } from '@/model/Sample.model';
import type { ID } from '@/model/Common.model';

const cellWidth = 180;

// Extended Sample type with API-returned fields
interface SampleWithGroup extends Sample {
  study_group_id?: ID;
}

interface MetadataField {
  id: string | number;
  title: string;
  type: MetadataGlossaryType;
}

interface FilterOption {
  name: string;
  id: number;
}

interface FilterData {
  types: Array<FilterOption>;
  users: Array<FilterOption>;
}

interface SampleMetadata extends MetadataPostItem {
  field_type?: string;
}

export const initialsColumns: Array<TanStackColumnDef<SampleWithGroup>> = [
  {
    id: 'sampleId',
    header: 'Sample ID',
    size: cellWidth,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: ({ row }) => row.original.sample_id || '-',
    isVisible: true,
  },
  {
    id: 'subject',
    header: 'Subject',
    size: cellWidth,
    enableSorting: true,
    cell: ({ row }) => row.original?.subject?.name ?? '-',
    isVisible: true,
  },
  {
    id: 'dateTaken',
    header: 'Date taken',
    size: cellWidth,
    enableSorting: true,
    cell: ({ row }) => <DateTimeRenderer value={row.original.date} defaultResponse="-" />,
    isVisible: true,
  },
  {
    id: 'type',
    header: 'Type',
    size: cellWidth,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: ({ row }) => row.original.type || '-',
    isVisible: true,
  },
  {
    id: 'group',
    header: 'Group',
    size: 275,
    enableSorting: true,
    style: { overflow: 'visible' },
    cell: ({ row }) => {
      if (_isNil(row.original.study_group_id)) {
        return '-';
      }
      return <GroupLabel group={row.original.study_group} />;
    },
    isVisible: true,
  },
];

const formatMetadata = (id: string | number, meta: Array<SampleMetadata> = []) => {
  const metaItem = meta.find((metadata) => metadata.glossary_id === id);
  return _notNil(metaItem) ? renderMetadataValue(metaItem as any) : '-';
};

const formatMetadataCell = (
  id: string | number,
  title: string,
  type: MetadataGlossaryType,
  columns?: Record<string | number, boolean>
): TanStackColumnDef<SampleWithGroup> => {
  const cellWidth = 180;
  return {
    id: String(id),
    header: title,
    enableSorting: false,
    isVisible: columns?.[id] ?? false,
    size: cellWidth,
    cell: ({ row }) => {
      const meta = type === 'group_meta' ? row.original.study_group?.metadata : row.original.metadata;
      return _isEmpty(meta) ? '-' : formatMetadata(id, meta);
    },
  };
};

export const buildColumns = (sampleMetdata: Array<MetadataField>): Array<TanStackColumnDef<SampleWithGroup>> => {
  const metadataColumns: Array<TanStackColumnDef<SampleWithGroup>> = [];

  sampleMetdata.forEach(({ id, title, type }) => metadataColumns.push(formatMetadataCell(id, title, type)));

  return [...initialsColumns, ...metadataColumns];
};

export const generateFilterOptions = (data?: FilterData | null) => {
  if (_notNil(data)) {
    const { users, types } = data;
    types.sort(sortAscByProperty('name'));
    users.sort(sortAscByProperty('name'));

    return [
      {
        value: 'type',
        name: 'Type',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: types,
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: types,
          },
        ],
      },
      {
        value: 'user',
        name: 'User',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: users,
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: users,
          },
        ],
      },
    ];
  }

  return [];
};
